import {
	AssignUserToSignatureBlock,
	BillingInfo,
	ClientContactBulkUpdateRequest,
	ClientContactBulkUpdateResponse,
	DocumentDetailsRequest,
	EditDocumentRequest,
	MergeTagVariable,
	PricingSummary,
	Template,
	UserCompany,
	UserData,
	VendorTemplateDetail
} from "@/Contracts";
import { Document } from "@/Contracts/Documents";
import { generateStore } from "@/Utils/Store";
import isEqual from "lodash/isEqual";
import React, { memo, useEffect } from "react";
import { combineReducers } from "redux";
import { ParentStateActionTypes } from "./actions/Parent";
import { ParentContextProvider } from "./context/ParentContext";
import { IParentContextProps } from "./context/ParentContext/interface";
import BlockReducer, { BlockState } from "./reducers/Block";
import ParentStateReducer, { ParentState } from "./reducers/Parent";
import { isVendorApp } from "@/Utils";

export interface ProposalBuilderState {
	blocks: BlockState;
	parentState: ParentState;
}

export const ProposalBuilderReducers = combineReducers({
	blocks: BlockReducer,
	parentState: ParentStateReducer
});

const {
	withProvider: withProposalBuilderProvider,
	store: proposalBuilderStore,
	useDispatch: useProposalBuilderDispatch,
	useSelector: useProposalBuilderSelector
} = generateStore<ProposalBuilderState>(ProposalBuilderReducers);

const defaultParentContext: Omit<
	IParentContextProps,
	"library" | "addBlocks" | "deleteBlocks" | "linkBlockList"
> = {
	document: {
		isDocumentLoading: false,
		activeDocumentPanel: null,
		previewPaymentSummary: null,
		previewPaymentTerms: null,
		getDocumentDetailsFromClient: function (
			_documentDetailsRequest: DocumentDetailsRequest
		): Promise<void> {
			throw new Error("Function not implemented.");
		},
		getDocumentDetails: function (
			_documentDetailsRequest: any
		): Promise<void> {
			throw new Error("Function not implemented.");
		},
		showSignFlowConfirmationModal: function (): void {
			throw new Error("Function not implemented.");
		},
		updateCurrentDocument: function (
			_updateCurrentDocumentRequest: EditDocumentRequest
		): Promise<void> {
			throw new Error("Function not implemented.");
		},
		updateDocumentPaymentSettings: function (
			_documentId: any,
			_documentPaymentUpdateRequest: any
		): Promise<void> {
			throw new Error("Function not implemented.");
		},
		assignSignatureBlockToRecipient: function (): Promise<Document> {
			throw new Error("Function not implemented.");
		},
		setDocumentSidePanel: function (_panelTab: any): void {
			throw new Error("Function not implemented.");
		},
		updateParentCurrentActiveDocument: function (
			_document: Document
		): void {
			throw new Error("Function not implemented.");
		}
	},
	template: {
		isTemplateLoading: false,
		activeTemplatePanel: null,
		updateSignatureAssignee: function (
			_userDetails: AssignUserToSignatureBlock
		): Promise<void> {
			throw new Error("Function not implemented.");
		},
		setTemplateSidePanel: function (_panelTab: any): void {
			throw new Error("Function not implemented.");
		},
		updateParentCurrentActiveTemplate: function (
			_document: Template | VendorTemplateDetail
		): void {
			throw new Error("Function not implemented.");
		}
	},
	mergeTags: {
		updateDocumentMergeTags: function (
			_mergeTagVariables: MergeTagVariable[]
		): Promise<{
			customMergeTagVariables?: MergeTagVariable[] | undefined;
		}> {
			throw new Error("Function not implemented.");
		},
		updateTemplateMergeTags: function (
			_mergeTagVariables: MergeTagVariable[]
		): Promise<{
			customMergeTagVariables?: MergeTagVariable[] | undefined;
		}> {
			throw new Error("Function not implemented.");
		}
	},
	assessment: {
		currentActiveAssessment: null,
		resetRiskScore: function (_assessmentId: string): Promise<void> {
			throw new Error("Function not implemented.");
		},
		editDetails: function (_assessment: any): Promise<void> {
			throw new Error("Function not implemented.");
		},
		updateCategory: function (_modifyCategoryRequest: any): Promise<void> {
			throw new Error("Function not implemented.");
		}
	},
	qbr: {
		resetRiskScore: function (_qbrId: string): Promise<void> {
			throw new Error("Function not implemented.");
		},
		editDetails: function (_qbr: any): Promise<void> {
			throw new Error("Function not implemented.");
		},
		updateCategory: function (_modifyCategoryRequest: any): Promise<void> {
			throw new Error("Function not implemented.");
		}
	},
	drawers: {
		AddClientContactDrawer: () => null,
		CloneProductToQuoteBlockDrawer: () => null,
		AddProductToQuoteBlockDrawer: () => null,
		AddProductDrawer: () => null
	},
	permissions: {
		hasAccess: false,
		hasSignatureBlockReadPermission: false,
		showZPricingPlanErrorTooltip: false,
		hasClientAddPermission: false,
		hasProductAddPermission: false,
		hasProductEditPermission: false,
		hasProductCreateAndAddPermission: false
	},
	client: {
		addClientContactDrawerOpen: false,
		addClientContactDrawerOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		addClientContactDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		},
		bulkUpdateClientContactEmail: function (
			_bulkUpdateRequest: ClientContactBulkUpdateRequest
		): Promise<{ results: ClientContactBulkUpdateResponse[] }> {
			throw new Error("Function not implemented.");
		}
	},
	featureFlag: {
		paymentSummaryFeatureFlag: false,
		hideReceiverMergeTagFeatureFlag: false,
		movePreferencesGlobal: false,
		enableCachePdf: false
	},
	user: {
		userCompany: new UserCompany(),
		userData: new UserData(),
		companyUserList: [],
		datePattern: "",
		billingInfo: new BillingInfo(),
		subscriptions: [],
		subscriptionDetails: new PricingSummary()
	},
	removePaymentSummaryBlockFromDocument: function (): Promise<void> {
		throw new Error("Function not implemented.");
	},
	entityData: {
		currentActiveEntity: null,
		documentType: undefined,
		legendLabels: {
			low_risk: "",
			medium_risk: "",
			high_risk: ""
		},
		manualIssuesList: [],
		ms365AutoAssmtIssuesList: [],
		categoryNamesList: []
	},
	allowZeroOrNegativeQuantity: {
		negativeQuantity: {
			name: undefined,
			value: undefined
		},
		zeroQuantity: {
			name: undefined,
			value: undefined
		}
	},
	pdf: {
		invalidatePDFUrl: function (): Promise<void> {
			throw new Error("Function not implemented.");
		},
		fetchDocumentPdfUrl: function (): Promise<string | undefined> {
			throw new Error("Function not implemented.");
		},
		isFetchingPDF: false
	}
};

const ProposalBuilderProvider: React.FC<
	IParentContextProps & { parentState: ParentState }
> = memo(
	({
		children,
		parentState,
		document = defaultParentContext.document,
		template = defaultParentContext.template,
		mergeTags = defaultParentContext.mergeTags,
		assessment = defaultParentContext.assessment,
		qbr = defaultParentContext.qbr,
		drawers = defaultParentContext.drawers,
		permissions = defaultParentContext.permissions,
		client = defaultParentContext.client,
		featureFlag = defaultParentContext.featureFlag,
		user = defaultParentContext.user,
		removePaymentSummaryBlockFromDocument = defaultParentContext.removePaymentSummaryBlockFromDocument,
		entityData = defaultParentContext.entityData,
		allowZeroOrNegativeQuantity = defaultParentContext.allowZeroOrNegativeQuantity,
		pdf = defaultParentContext.pdf,
		...rest
	}) => {
		const { currentActiveDocument, currentActiveTemplate } =
			useProposalBuilderSelector((state: ProposalBuilderState) => ({
				currentActiveDocument: state.parentState.currentActiveDocument,
				currentActiveTemplate: state.parentState.currentActiveTemplate
			}));
		const dispatch = useProposalBuilderDispatch();
		useEffect(() => {
			dispatch({
				type: ParentStateActionTypes.UPDATE_PARENT_STATE,
				parentState: {
					...parentState
				}
			});
		}, [
			parentState.currentActiveDocument,
			parentState.currentActiveTemplate,
			parentState.taxCategoriesList,
			parentState.taxRegionsList
		]);
		useEffect(() => {
			if (!parentState.currentActiveDocument || !currentActiveDocument)
				return;
			const parentLatestRevision = Document.getLatestRevision(
				parentState.currentActiveDocument
			);
			const latestRevision = Document.getLatestRevision(
				currentActiveDocument
			);
			if (
				!isEqual(parentLatestRevision, latestRevision) ||
				currentActiveDocument.payment?.amount_to_be_paid !==
					parentState.currentActiveDocument.payment?.amount_to_be_paid
			) {
				document.updateParentCurrentActiveDocument(
					currentActiveDocument
				);
			}
		}, [currentActiveDocument]);

		useEffect(() => {
			if (!parentState.currentActiveTemplate || !currentActiveTemplate)
				return;
			if (
				!isEqual(
					parentState.currentActiveTemplate.page_details,
					currentActiveTemplate.page_details
				)
			) {
				if (isVendorApp())
					template.updateParentCurrentActiveTemplate(
						currentActiveTemplate as VendorTemplateDetail
					);
				else
					template.updateParentCurrentActiveTemplate(
						currentActiveTemplate as Template
					);
			}
		}, [currentActiveTemplate]);

		return (
			<ParentContextProvider
				document={document}
				template={template}
				mergeTags={mergeTags}
				assessment={assessment}
				qbr={qbr}
				drawers={drawers}
				permissions={permissions}
				client={client}
				featureFlag={featureFlag}
				user={user}
				removePaymentSummaryBlockFromDocument={
					removePaymentSummaryBlockFromDocument
				}
				entityData={entityData}
				allowZeroOrNegativeQuantity={allowZeroOrNegativeQuantity}
				pdf={pdf}
				{...rest}
			>
				{children}
			</ParentContextProvider>
		);
	}
);

export {
	ProposalBuilderProvider,
	defaultParentContext,
	withProposalBuilderProvider,
	proposalBuilderStore,
	useProposalBuilderDispatch,
	useProposalBuilderSelector
};
